<script setup>
/**
 * AppCheckbox — the single checkbox atom for CDP 2.0.
 *
 * Wraps the legacy `.checkbox-controller` class so EVERY checkbox in the app
 * shares identical styling + focus / disabled states: the Orders & Sale Pick
 * List filters (via AppMarketplaceFilter / AppMultiSelectFilter), the Inventory
 * & Batches select cells, the Filters menu, Settings, Auth, Connections…
 *
 * Bound via v-model (boolean). Also usable in "controlled" mode with
 * `:model-value` + `@change` (the pattern the multi-select filters use, where
 * the parent owns the checked state and `@change` toggles a store/array).
 *
 *   <AppCheckbox v-model="agree" />                       ← v-model
 *   <AppCheckbox :model-value="isSelected(v)" @change="toggle(v)" />  ← controlled
 */
import { onMounted, ref, watch } from 'vue'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue:    { type: Boolean, default: false },
  disabled:      { type: Boolean, default: false },
  /** Tri-state dash — set on the DOM node (not a reflected HTML attribute). */
  indeterminate: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue', 'change'])

const inputRef = ref(null)

// `indeterminate` is a DOM property only, so sync it imperatively.
const syncIndeterminate = () => {
  if (inputRef.value) inputRef.value.indeterminate = props.indeterminate
}
onMounted(syncIndeterminate)
watch(() => props.indeterminate, syncIndeterminate)

const onChange = (e) => {
  emit('update:modelValue', e.target.checked)
  emit('change', e)
}
</script>

<template>
  <input
    ref="inputRef"
    type="checkbox"
    class="checkbox-controller shrink-0"
    :class="disabled ? 'cursor-not-allowed' : 'cursor-pointer'"
    :checked="modelValue"
    :disabled="disabled"
    v-bind="$attrs"
    @change="onChange"
  />
</template>
